import {
  AutoClosingDropdown,
  CheckIcon,
  EditIcon,
  FormSection,
  ModalTypeEnum,
  TextInput,
  TrashIcon,
  UndoIcon,
  useAlert,
  useModal,
  SecondaryPageTitle,
} from "@wit/mpesa-ui-components";
import { DropdownType } from "@wit/mpesa-ui-components/lib/components/dropdown/dropdown.component";
import styleTheme from "@wit/mpesa-ui-components/lib/configs/theme.config";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { BaseModalProps } from "@wit/mpesa-ui-components/lib/context/modal/modal.types";
import { Formik, FormikErrors, FormikHelpers } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import store, { IStoreInterface } from "../../../../configs/store.config";
import { RoutesEnum } from "../../../../routes/routes.constants";
import SafaricomDeleteModal from "../../../../shared/components/safaricom-delete-modal/safaricom-delete-modal.component";
import { IIdentityDocument } from "../../../../shared/models/identity.model";
import { PageContent } from "../../../../shared/responsive-ui.styled";
import { LoadingText, PageContainer } from "../../../../shared/shared.styled";
import IdentityApi from "../identity.api";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { unicodeChars } from "../../../../shared/shared.utils";
// import "../../../admin/users/users.css";
import { IUpdateIdentityRequest, StatusEnums, TypeEnums, CaptureBackSideEnums } from "../identity.interfaces";
import { IdentityActions } from "../identity.store";
import { normalizeErrorMessage } from "../../../../shared/utils/errorMessageHelper.utils";
import { cleanBackendMessage } from "../../../../shared/utils/cleanBackendErrorMessage.utils";

/**
 * User profile page
 */
const IdentityDocPage = () => {
  const { t, i18n } = useTranslation(["private", "market_values"]);
  const dispatch = useDispatch();
  const history = useHistory();
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const location = useLocation<{ urn?: string }>();
  const urn = location.state?.urn;
  const state = useSelector((store: IStoreInterface) => store);

  const identityValidationSchema = Yup.object().shape({
    en: Yup.string()
      .trim()
      .required(t("pages.addIdentity.errors.requiredField"))
      .min(3, t("pages.addIdentity.errors.requiredMinLength"))
      .max(70, t("pages.addIdentity.errors.requiredMaxLength"))
      .test("only-letters", t("pages.addIdentity.errors.requiredAlphabets"), value =>
        value ? unicodeChars(value) : false,
      ),
    fr: Yup.string()
      .trim()
      .required(t("pages.addIdentity.errors.requiredField"))
      .min(3, t("pages.addIdentity.errors.requiredMinLength"))
      .max(70, t("pages.addIdentity.errors.requiredMaxLength")),
    // .test("only-letters", t("pages.addIdentity.errors.requiredAlphabets"), value =>
    //   value ? unicodeChars(value) : false,
    // ),
  });

  const user = useSelector((store: IStoreInterface) => store.identitiesReducer.users.find(user => user.urn === urn));
  const [showConfirmationModal, hideConfirmationModal, setConfirmationProps] = useModal(
    ModalTypeEnum.ConfirmationModal,
  );
  const [showRemoveDocumentModal, hideRemoveDocumentModal] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    user && (
      <SafaricomDeleteModal
        secondaryAction={() => hideRemoveDocumentModal()}
        primaryAction={() => removeDocument(hideRemoveDocumentModal)}
        title={t("pages.addIdentity.formSection.removeOperator")}
        description={t("pages.addIdentity.modals.removeDocumentConfirmation", {
          user: user.urn,
        })}
        primaryBtnLabel={t("pages.addIdentity.modals.removeUserBtn")}
      />
    ),
  );

  const [isEditing, setIsEditing] = React.useState(false);

  useEffect(() => {
    if (urn) {
      IdentityApi.methods.getSingleIdentity(urn).then(res => {
        dispatch(IdentityActions.creators.selectDocument(res.data));
      });
    }
  }, [urn]);

  const removeDocument = (hideModal: () => void) => {
    if (!user) {
      return;
    }
    IdentityApi.methods
      .deleteIdentity(user.urn)
      .then(() => {
        setAlertProps({
          title: t("pages.addIdentity.alerts.removeDocumentSuccess"),
          type: AlertTypeEnum.SUCCESS,
        });
        showAlert();
        history.push(RoutesEnum.MANAGE_IDENTITY_DOCS);
      })
      .catch((error: any) => {
        let backendMessage = error?.data?.status?.message || t("pages.addIdentity.alerts.removeUserError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      })
      .finally(hideModal);
  };

  const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

  /**
   * Edit user request
   * @param values
   * @param actions
   */
  const editIdentityDocument = (values: IUpdateIdentityRequest, actions: FormikHelpers<any>) => {
    if (!user) {
      return;
    }
    const payload: IUpdateIdentityRequest = {
      ...values,
      en: toTitleCase((values.en ?? "").trim()),
      fr: (values.fr ?? "").trim(),
      captureBackSide: values.captureBackSide,
    };
    setConfirmationProps({
      title: t("pages.addIdentity.modals.saveChanges"),
      description: t("pages.addIdentity.modals.confirmEdit", {
        user: user.urn,
      }),
      primaryBtnId: "confirm-button",
      secondaryBtnId: "cancel-button",
      primaryBtnLabel: t("pages.addIdentity.modals.primaryBtnLabel"),
      secondaryBtnLabel: t("pages.addIdentity.modals.secondaryBtnLabel"),
      primaryAction: () => {
        actions.setSubmitting(true);
        IdentityApi.methods
          .updateIdentity(user.urn, payload)
          .finally(() => actions.setSubmitting(false))
          .then(() => {
            setAlertProps({
              title: t("pages.addIdentity.alerts.editSuccess"),
              type: AlertTypeEnum.SUCCESS,
            });
            showAlert();
            setIsEditing(false);
            dispatch(IdentityActions.creators.editDocumentsAction(user.urn, values));
            dispatch(IdentityActions.creators.selectDocument(user.urn));
          })
          .catch((error: any) => {
            let backendMessage = error?.data?.status?.message || t("pages.addIdentity.alerts.editError");
            backendMessage = cleanBackendMessage(backendMessage);
            const httpCode = error?.data?.status?.httpCode;
            backendMessage = normalizeErrorMessage(backendMessage, httpCode);
            setAlertProps({
              title: backendMessage,
              type: AlertTypeEnum.ERROR,
            });
            showAlert();
          })
          .finally(() => {
            actions.setSubmitting(false);
            hideConfirmationModal();
          });
      },
      secondaryAction: () => hideConfirmationModal(),
    } as BaseModalProps);
    showConfirmationModal();
  };

  /**
   * method to return profile page form rows
   */
  const getProfileFormRows = (
    values: {
      documentType?: string;
      type?: string;
      en?: string;
      fr?: string;
      status?: StatusEnums;
      documentStandard?: TypeEnums;
      documentId?: string;
      captureBackSide?: boolean;
    },
    handleChange: (e: React.ChangeEvent<HTMLElement>) => void,
    setFieldValue: (field: string, value: any) => void,
    errors: FormikErrors<IUpdateIdentityRequest>,
    touched: Partial<{ [K in keyof IUpdateIdentityRequest]: boolean }>,
  ) => {
    const rows = user
      ? [
          {
            label: <IdentityLabel id="documentId">{t("pages.addIdentity.formSection.documentIdLabel")}</IdentityLabel>,
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentId-value">{user.documentId}</NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentId-value">{user.documentId}</NonEditableText>
              </TextOverflowWrapper>
            ),
          },
          {
            label: <IdentityLabel id="documentType">{t("pages.addIdentity.formSection.documentType")}</IdentityLabel>,
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentType-value">{user.documentType}</NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentType-value">{user.documentType}</NonEditableText>
              </TextOverflowWrapper>
            ),
          },
          {
            label: <IdentityLabel id="en">{t("pages.addIdentity.formSection.englishNameLabel")}</IdentityLabel>,
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="en-value">{user.en}</NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <TextInput
                name="en"
                id="en-value"
                value={values.en}
                onChange={handleChange}
                error={errors.en}
                placeholder={t("pages.addIdentity.formSection.englishPlaceholder")}
              />
            ),
          },
          {
            label: <IdentityLabel id="fr">{t("pages.addIdentity.formSection.frenchNameLabel")}</IdentityLabel>,
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="fr-value">{user.fr}</NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <TextInput
                name="fr"
                id="fr-value"
                value={values.fr}
                onChange={handleChange}
                error={errors.fr}
                placeholder={t("pages.addIdentity.formSection.frenchPlaceholder")}
              />
            ),
          },
          {
            label: <IdentityLabel id="status">{t("pages.addIdentity.formSection.statusLabel")}</IdentityLabel>,
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="status-value">
                  {user.status ? t(`commons.identityStatus.${user.status}`) : ""}
                </NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <div id={`status-dropdown`}>
                <AutoClosingDropdown
                  options={[
                    {
                      key: StatusEnums.ACTIVE,
                      label: t("commons.identityStatus.ACTIVE"),
                    },
                    {
                      key: StatusEnums.INACTIVE,
                      label: t("commons.identityStatus.INACTIVE"),
                    },
                  ]}
                  dropdownType={DropdownType.RECTANGULAR_NORMAL}
                  selectOption={opt => setFieldValue("status", opt.key)}
                  label={
                    values.status
                      ? t(`commons.identityStatus.${values.status}`)
                      : t("pages.addIdentity.formSection.statusPlaceholder")
                  }
                  hasValue={!!values.status}
                />
              </div>
            ),
          },
          {
            label: (
              <IdentityLabel id="documentStandard">{t("pages.addIdentity.formSection.docStandardLabel")}</IdentityLabel>
            ),
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentStandard-value">{user.documentStandard}</NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="documentStandard-value">{user.documentStandard}</NonEditableText>
              </TextOverflowWrapper>
            ),
          },
          {
            label: (
              <IdentityLabel id="captureBackSide">{t("pages.addIdentity.formSection.captureBackLabel")}</IdentityLabel>
            ),
            displayComponent: (
              <TextOverflowWrapper>
                <NonEditableText id="captureBackSide-value">
                  {String(user.captureBackSide)
                    .charAt(0)
                    .toUpperCase() + String(user.captureBackSide).slice(1)}
                </NonEditableText>
              </TextOverflowWrapper>
            ),
            editingComponent: (
              <div id={`captureBackSide-dropdown`}>
                <AutoClosingDropdown
                  options={[
                    {
                      key: CaptureBackSideEnums.TRUE,
                      label: t("commons.captureStatusType.TRUE"),
                    },
                    {
                      key: CaptureBackSideEnums.FALSE,
                      label: t("commons.captureStatusType.FALSE"),
                    },
                  ]}
                  dropdownType={DropdownType.RECTANGULAR_NORMAL}
                  selectOption={opt => setFieldValue("captureBackSide", opt.key === CaptureBackSideEnums.TRUE)}
                  label={
                    values.captureBackSide ? t("commons.captureStatusType.TRUE") : t("commons.captureStatusType.FALSE")
                  }
                  hasValue={typeof values.captureBackSide === "boolean"}
                />
              </div>
            ),
          },
        ]
      : [];

    return rows;
  };

  return (
    <PageContainer>
      {user ? (
        <>
          <SecondaryPageTitle
            goBackFn={() => window.history.back()}
            title={i18n.language === "fr" ? user.fr : user.en}
          />
          <PageContent>
            <Formik
              initialValues={{
                en: user.en,
                fr: user.fr,
                status: user.status,
                documentStandard: user.documentStandard,
                documentId: user.documentId,
                captureBackSide: Boolean(user.captureBackSide),
              }}
              onSubmit={editIdentityDocument}
              validateOnChange={true}
              validateOnBlur={true}
              validationSchema={identityValidationSchema}
            >
              {({ values, handleChange, setFieldValue, handleSubmit, resetForm, errors, touched, isValid }) => (
                <>
                  <MainContent className="main-content">
                    <form onSubmit={handleSubmit}>
                      <FormSection
                        isEditing={isEditing}
                        title={t("pages.addIdentity.formSection.title")}
                        rows={getProfileFormRows(values, handleChange, setFieldValue, errors, touched)}
                      />
                    </form>
                  </MainContent>
                  <SideMenu className="side-menu">
                    {!isEditing ? (
                      <LinkContainer
                        id="edit-operator-document"
                        onClick={() => {
                          setIsEditing(!isEditing);
                        }}
                      >
                        <IconContainer color={styleTheme.palette.turquoiseBlue}>
                          <EditIcon />
                        </IconContainer>
                        <span>{t("pages.addIdentity.formSection.editIdentityDocument")}</span>
                      </LinkContainer>
                    ) : (
                      <>
                        <LinkContainer
                          id="save-changes-button"
                          onClick={() => handleSubmit()}
                          style={{
                            pointerEvents: !isValid ? "none" : "auto",
                            opacity: !isValid ? 0.5 : 1,
                          }}
                        >
                          <IconContainer color={styleTheme.palette.successGreen}>
                            <CheckIcon />
                          </IconContainer>
                          <span>{t("pages.addIdentity.formSection.saveChanges")}</span>
                        </LinkContainer>

                        <LinkContainer
                          id="cancel-changes-button"
                          onClick={() => {
                            resetForm();
                            setIsEditing(false);
                          }}
                        >
                          <IconContainer color={styleTheme.palette.vodafoneRed}>
                            <UndoIcon />
                          </IconContainer>
                          <span>{t("pages.addIdentity.formSection.cancelChanges")}</span>
                        </LinkContainer>
                      </>
                    )}
                    <LinkContainer
                      id="remove-operator-button"
                      onClick={() => showRemoveDocumentModal()}
                      disabled={isEditing}
                    >
                      <IconContainer color={styleTheme.palette.vodafoneRed}>
                        <TrashIcon />
                      </IconContainer>
                      <span>{t("pages.addIdentity.formSection.removeOperator")}</span>
                    </LinkContainer>
                  </SideMenu>
                </>
              )}
            </Formik>
          </PageContent>
        </>
      ) : (
        <LoadingText>{t("commons.loadingResults")}</LoadingText>
      )}
    </PageContainer>
  );
};

export default IdentityDocPage;

const MainContent = styled("div")`
  display: flex;
  flex-direction: column;
  width: 60%;
`;

const SideMenu = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: 55px;
  width: 40%;
  margin-left: 75px;
`;

const LinkContainer = styled("div")<{ disabled?: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  font-family: Vodafone Rg;
  color: ${styleTheme.palette.darkGrey};
  font-size: 16px;
  margin-bottom: 15px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? "none" : "inherit")};

  > div {
    margin-right: 8px;
  }
`;

const IconContainer = styled("div")<{ color: string }>`
  width: 20px;
  height: 20px;
  > svg {
    stroke: ${props => props.color};
  }
`;

const UserStatus = styled("div")<{ color: any }>`
  font-family: Vodafone Rg;
  font-size: 16px;
  color: ${props => props.color};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
`;

const NonEditableText = styled("span")`
  font-family: Vodafone Rg;
  font-size: 16px;
  color: ${styleTheme.palette.darkGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IdentityLabel = styled("span")`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: bold;
  color: #999999;
`;

const TextOverflowWrapper = styled("div")`
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Test = styled("div")`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 128px;
  background-color: #fafafa;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding-left: 278px;
  z-index: 1;
`;

const TestSpan = styled("span")`
  font-family: Vodafone Lt;
  font-size: 38px;
  color: #333333;
  margin-left: 0px;
`;
